import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { TrendingUp, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMyTier } from "@/hooks/use-my-tier";

interface UsageProjection {
  trackedRevenueCents: number;
  sharePercent: number;
  projectedChargeCents: number;
  proMonthlyCents: number | null;
  upsellThresholdCents: number | null;
  shouldShowUpsell: boolean;
  isLiteTier: boolean;
  dismissedRecently: boolean;
  showBanner: boolean;
}

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

// Smart upsell banner shown to Lite-tier workspaces when their projected
// monthly revenue-share charge crosses 80% of the Pro tier's monthly price.
// Suppressed for 7 days after dismissal.
export function LiteUpsellBanner() {
  const { data: myTier } = useMyTier();
  const workspaceId = myTier?.workspaceId;

  const { data: projection } = useQuery<UsageProjection>({
    queryKey: ["/api/lite/usage-projection", workspaceId],
    enabled: !!workspaceId && myTier?.tier === "lite",
    refetchInterval: 5 * 60 * 1000, // Re-check every 5 min
  });

  const dismiss = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/lite/dismiss-upsell", { workspaceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lite/usage-projection", workspaceId] });
    },
  });

  if (!projection?.showBanner || !projection.proMonthlyCents) return null;

  return (
    <div
      className="border-b border-blue-200 dark:border-blue-900 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 px-4 py-2.5"
      data-testid="banner-lite-upsell"
    >
      <div className="flex items-center gap-3 max-w-7xl mx-auto">
        <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-medium">You're on track to pay {formatDollars(projection.projectedChargeCents)} this month</span>
          <span className="text-muted-foreground">
            {" "}— that's close to the Pro plan ({formatDollars(projection.proMonthlyCents)}/mo flat).
            Upgrading would cap your costs.
          </span>
        </div>
        <Link href="/upgrade">
          <Button size="sm" className="gap-1.5 shrink-0" data-testid="button-upsell-view-plans">
            <TrendingUp className="h-3.5 w-3.5" />
            View plans
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          aria-label="Dismiss for 7 days"
          data-testid="button-upsell-dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
